import { toast } from "react-semantic-toasts";

import {
    AUTH_SIGNIN_REQUEST,
    AUTH_SIGNIN_SUCCESS,
    AUTH_SIGNIN_INPUT_CHANGE,
    AUTH_SIGNIN_INPUT_ERROR,
    AUTH_SIGNUP_REQUEST,
    AUTH_SIGNUP_SUCCESS,
    AUTH_SIGNUP_INPUT_CHANGE,
    AUTH_SIGNUP_INPUT_ERROR,
    AUTH_RESET_INPUT_CHANGE,
    AUTH_RESET_INPUT_ERROR,
    AUTH_TOGGLE_PASSWORD_FIELD,
    AUTH_TOGGLE_NEW_PASSWORD_FIELD,
    AUTH_TOGGLE_CONFIRM_PASSWORD_FIELD,
    AUTH_SIGNOUT,
    AUTH_EXPIRE,
    AUTH_PROFILE_UPDATE_ERROR,
    AUTH_PROFILE_UPDATING,
    AUTH_PROFILE_UPDATED,
    AUTH_RESET_REQUEST,
    AUTH_RESET_FINISH,
    AUTH_CHANGE_PASSWORD_INPUT_CHANGE,
    AUTH_CHANGE_PASSWORD_INPUT_ERROR,
    AUTH_CHANGE_PASSWORD_REQUEST,
    AUTH_CHANGE_PASSWORD_FINISH,
} from "../constants";
import { persistor } from "../../store";
import { apiCallError } from "../../common/actions";
import { loadCompanyLogos } from "../../company/logo/actions";
import { loadCustomerList } from "../../customer/actions/list";
import {
    signin,
    resetRequest,
    signup,
    validateSigninData,
    validateToken,
    signout,
    validateSignupData,
    validateProfileData,
    updateProfile,
    validateResetRequestData,
    validatePasswordChangeRequestData,
    changePasswordRequest,
} from "../services/api";
import { trackPasswordResetRequest, trackPasswordChangeFailure } from "../../common/analytics";

export const authSignin = credentials => dispatch => {
    const { error, value } = validateSigninData(credentials);

    if (error) {
        const payload = {};
        error.details.forEach(e => (payload[e.path[0]] = e.message));
        return dispatch({
            type: AUTH_SIGNIN_INPUT_ERROR,
            payload,
        });
    }

    dispatch({
        type: AUTH_SIGNIN_REQUEST,
    });

    signin(value)
        .then(result => {
            dispatch({
                type: AUTH_SIGNIN_SUCCESS,
                payload: result,
            });
            dispatch(loadCustomerList());
            dispatch(loadCompanyLogos());

            return;
        })
        .catch(err => {
            if (err.response && err.response.status === 400) {
                return dispatch({
                    type: AUTH_SIGNIN_INPUT_ERROR,
                    payload: { password: err.response.data.message },
                });
            } else {
                dispatch({
                    type: AUTH_SIGNIN_INPUT_ERROR,
                    payload: { password: "our servers broke, please try again later" },
                });
                return dispatch(apiCallError(err));
            }
        });
};

export const authSignup = credentials => dispatch => {
    const { error, value } = validateSignupData(credentials);

    if (error) {
        const payload = {};
        error.details.forEach(e => (payload[e.path[0]] = e.message));
        return dispatch({
            type: AUTH_SIGNUP_INPUT_ERROR,
            payload,
        });
    }

    dispatch({
        type: AUTH_SIGNUP_REQUEST,
    });

    signup(value)
        .then(result => {
            dispatch({
                type: AUTH_SIGNUP_SUCCESS,
                payload: result,
            });
        })
        .catch(err => {
            if (err.response.status === 400) {
                return dispatch({
                    type: AUTH_SIGNUP_INPUT_ERROR,
                    payload: { password: err.response.data.message },
                });
            }

            dispatch(apiCallError(err));
        });
};

export const authValidate = () => dispatch => {
    dispatch({
        type: AUTH_SIGNIN_REQUEST,
    });

    validateToken()
        .then(user => {
            dispatch(loadCustomerList());
            return dispatch({
                type: AUTH_SIGNIN_SUCCESS,
                payload: user,
            });
        })
        .catch(() => {
            return dispatch({
                type: AUTH_SIGNOUT,
            });
        });
};

export const authSigninInputChange = (field, value) => dispatch => {
    dispatch({
        type: AUTH_SIGNIN_INPUT_CHANGE,
        payload: { [field]: value },
    });
};

export const authSignupInputChange = (field, value) => dispatch => {
    dispatch({
        type: AUTH_SIGNUP_INPUT_CHANGE,
        payload: { [field]: value },
    });
};

export const authResetInputChange = (field, value) => dispatch => {
    dispatch({
        type: AUTH_RESET_INPUT_CHANGE,
        payload: { [field]: value },
    });
};

export const authChangePasswordInputChange = (field, value) => dispatch => {
    dispatch({
        type: AUTH_CHANGE_PASSWORD_INPUT_CHANGE,
        payload: { [field]: value },
    });
};

export const authResetRequest = credentials => dispatch => {
    const { error, value } = validateResetRequestData(credentials);

    if (error) {
        const payload = {};
        error.details.forEach(e => (payload[e.path[0]] = e.message));
        return dispatch({
            type: AUTH_RESET_INPUT_ERROR,
            payload,
        });
    }

    dispatch({
        type: AUTH_RESET_REQUEST,
    });

    trackPasswordResetRequest(value.email);

    resetRequest(value)
        .then(() => {
            dispatch({
                type: AUTH_RESET_FINISH,
                payload: {
                    success: true,
                },
            });

            toast({
                description: `Email has been sent to your inbox. The password reset link will be expired after 23 hours.`,
                time: 3000,
                type: "success",
                icon: "checkmark",
                title: "Success",
            });
        })
        .catch(err => {
            if (err.response && err.response.status === 400) {
                toast({
                    description: err.response.data.message,
                    time: 3000,
                    type: "error",
                    icon: "warning sign",
                    title: "Error",
                });
            } else {
                toast({
                    description: `Error occurred while sending email`,
                    time: 3000,
                    type: "error",
                    icon: "warning sign",
                    title: "Error",
                });
            }

            dispatch({
                type: AUTH_RESET_FINISH,
                payload: {
                    success: false,
                },
            });

            return dispatch(apiCallError(err));
        });
};

export const authChangePasswordRequest = credentials => dispatch => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("reset-token");

    const { error, value } = validatePasswordChangeRequestData(credentials);

    if (error) {
        const payload = {};
        error.details.forEach(e => (payload[e.path[0]] = e.message));
        return dispatch({
            type: AUTH_CHANGE_PASSWORD_INPUT_ERROR,
            payload,
        });
    }

    if (credentials.newPassword !== credentials.confirmPassword) {
        return dispatch({
            type: AUTH_CHANGE_PASSWORD_INPUT_ERROR,
            payload: {
                Password: "Passwords don't match",
            },
        });
    }

    dispatch({
        type: AUTH_CHANGE_PASSWORD_REQUEST,
    });

    changePasswordRequest({
        token,
        password: value.newPassword,
    })
        .then(() => {
            dispatch({
                type: AUTH_CHANGE_PASSWORD_FINISH,
                payload: {
                    success: true,
                },
            });

            toast({
                description: `Password reset successfully.`,
                time: 3000,
                type: "success",
                icon: "checkmark",
                title: "Success",
            });

            setTimeout(() => {
                window.location.href = "/auth/signin";
            }, 1000);
        })
        .catch(err => {
            if (err.response && err.response.status === 400) {
                trackPasswordChangeFailure(err.response.data.message);

                toast({
                    description: err.response.data.message,
                    time: 3000,
                    type: "error",
                    icon: "warning sign",
                    title: "Error",
                });
            } else {
                toast({
                    description: `Error occurred while resetting password.`,
                    time: 3000,
                    type: "error",
                    icon: "warning sign",
                    title: "Error",
                });
            }

            dispatch({
                type: AUTH_CHANGE_PASSWORD_FINISH,
                payload: {
                    success: false,
                },
            });
            return dispatch(apiCallError(err));
        });
};

export const authSignout = () => dispatch => {
    return signout()
        .then(
            () => dispatch({ type: AUTH_SIGNOUT }),
            () => dispatch({ type: AUTH_SIGNOUT })
        )
        .then(() => persistor.purge());
};

export const authTogglePasswordField = () => dispatch =>
    dispatch({
        type: AUTH_TOGGLE_PASSWORD_FIELD,
    });

export const authToggleNewPasswordField = () => dispatch =>
    dispatch({
        type: AUTH_TOGGLE_NEW_PASSWORD_FIELD,
    });

export const authToggleConfirmPasswordField = () => dispatch =>
    dispatch({
        type: AUTH_TOGGLE_CONFIRM_PASSWORD_FIELD,
    });

export const authExpire = () => ({
    type: AUTH_EXPIRE,
});

export const authUpdateProfile = profile => dispatch => {
    const { error, value } = validateProfileData(profile);
    if (error) {
        const payload = {};
        error.details.forEach(e => (payload[e.path[0]] = e.message));

        toast({
            description: Object.values(payload)[0],
            type: "error",
            icon: "warning sign",
            title: "Error",
        });

        dispatch({
            type: AUTH_PROFILE_UPDATE_ERROR,
            payload,
        });

        return null;
    }

    dispatch({
        type: AUTH_PROFILE_UPDATING,
        payload: true,
    });

    return updateProfile(value)
        .then(user => {
            dispatch({
                type: AUTH_PROFILE_UPDATED,
                payload: user,
            });

            return true;
        })
        .catch(err => {
            dispatch(apiCallError(err));
            return false;
        });
};
